import { projectTypes, projectGoals, projectStages, updateAnswer, getStartingPoint, canTransferSummary } from "./project-finder-model.mjs";
import { browserSessionStorage, saveProjectHandoff, readProjectHandoff, clearProjectHandoff } from './project-handoff.mjs';

export function initProjectFinder(onLayoutChange = () => {}) {
  const root = document.querySelector("[data-project-finder]");
  if (!root) return;
  const find = (name) => root.querySelector(`[data-finder-${name}]`);
  const back = find("back"), next = find("next"), options = find("options");
  const actions = find("result-actions"), summary = find("summary");
  const storage = browserSessionStorage();
  let answers = { type: "", goal: "", stage: "" };
  let current = 0, revealed = false;
  const keys = ["type", "goal", "stage"];
  const questions = ["What are you planning?", "What should it help you do?", "Where are you starting?"];
  const stages = ["Project type", "Your goal", "Starting stage"];
  const choices = () => [projectTypes, projectGoals[answers.type] || [], projectStages][current];

  function hideResult() {
    revealed = false;
    actions.hidden = summary.hidden = true;
    find("title").textContent = "A little clarity, before we build.";
    find("body").textContent = "Choose your project type, goal and starting stage. We’ll suggest a direction you can discuss with us.";
    find("status").textContent = "";
    find('copy-wrap').hidden = true;
  }

  function render(focus = false) {
    find("count").textContent = `${String(current + 1).padStart(2, "0")} / 03`;
    find("stage").textContent = stages[current];
    find("progress").value = current + 1;
    find("question").textContent = questions[current];
    options.replaceChildren(...choices().map((item) => {
      const label = document.createElement("label");
      label.className = "finder-option";
      const radio = document.createElement("input");
      radio.type = "radio"; radio.name = `finder_${keys[current]}`;
      radio.value = item.value; radio.checked = answers[keys[current]] === item.value;
      const copy = document.createElement("span");
      const title = document.createElement("strong"); title.textContent = item.label;
      copy.append(title);
      if (item.detail) { const detail = document.createElement("span"); detail.textContent = item.detail; copy.append(detail); }
      label.append(radio, copy);
      return label;
    }));
    back.disabled = current === 0;
    next.disabled = !answers[keys[current]] || revealed;
    next.textContent = current === 2 ? "See starting point" : "Next";
    if (focus) find("question").focus();
    onLayoutChange();
  }

  options.addEventListener("change", (event) => {
    if (!event.target.matches('input[type="radio"]')) return;
    answers = updateAnswer(answers, keys[current], event.target.value);
    hideResult();
    next.disabled = false;
  });
  back.addEventListener("click", () => { if (current > 0) { current--; hideResult(); render(true); } });
  next.addEventListener("click", () => {
    if (!answers[keys[current]] || revealed) return;
    if (current < 2) { current++; render(true); return; }
    const result = getStartingPoint(answers);
    if (!result) return;
    revealed = true;
    next.disabled = true;
    find("title").textContent = result.title;
    find("body").textContent = result.body;
    summary.replaceChildren(...result.rows.map(([name, value]) => {
      const row = document.createElement("div"), dt = document.createElement("dt"), dd = document.createElement("dd");
      dt.textContent = name; dd.textContent = value; row.append(dt, dd); return row;
    }));
    actions.hidden = summary.hidden = false;
    find("title").focus();
    if (window.matchMedia("(max-width: 759px)").matches) find("title").scrollIntoView({ block: "start", behavior: "auto" });
    onLayoutChange();
  });
  find("edit").addEventListener("click", () => { current = 0; hideResult(); render(true); });
  find("reset").addEventListener("click", () => {
    clearProjectHandoff(storage);
    answers = { type: "", goal: "", stage: "" }; current = 0;
    hideResult(); render(true);
    find("status").textContent = "Finder answers reset. Any contact draft is unchanged.";
  });
  find("discuss").addEventListener("click", event => {
    const result = getStartingPoint(answers);
    if (!result) { event.preventDefault(); return; }
    if (!saveProjectHandoff(storage, answers)) {
      event.preventDefault();
      find('copy').value = result.summary;
      find('copy-wrap').hidden = false;
      find('status').textContent = 'Your browser could not carry the summary to Contact. Copy it below, then continue. Nothing has been sent.';
      find('copy').focus();
      onLayoutChange();
    }
  });
  root.hidden = false;
  render();
}

export function initContactHandoff(onLayoutChange = () => {}) {
  const form = document.querySelector('[data-project-enquiry]');
  if (!form) return;
  const summary = form.querySelector('[data-contact-summary]');
  const wrap = form.querySelector('[data-contact-summary-wrap]');
  const type = form.querySelector('[data-contact-type]');
  const storage = browserSessionStorage();
  let lastGenerated = '', lastType = '';
  const restore = () => {
    if (summary.value.trim()) wrap.hidden = false;
    const result = readProjectHandoff(storage);
    if (!result) return;
    if (canTransferSummary(summary.value, lastGenerated) || summary.value === result.summary) {
      // Preserve manually selected types, messages and all other customer inputs.
      if (!type.value || type.value === lastType) type.value = result.type;
      summary.value = lastGenerated = result.summary;
      lastType = result.type;
      wrap.hidden = false;
      onLayoutChange();
    }
  };
  restore();
  window.addEventListener('pageshow', restore);
  form.querySelector('[data-contact-summary-remove]').addEventListener('click', () => {
    clearProjectHandoff(storage);
    summary.value = lastGenerated = ''; lastType = ''; wrap.hidden = true;
    summary.dispatchEvent(new Event('input', { bubbles: true }));
    type.focus({ preventScroll: true });
    onLayoutChange();
  });
  form.addEventListener('skai:enquiry-saved', () => clearProjectHandoff(storage));
}

export function initProjectEnquiry(onLayoutChange = () => {}) {
  const form = document.querySelector("[data-project-enquiry]");
  if (!form || !window.fetch || !window.FormData) return;
  const submit = form.querySelector("[data-project-submit]");
  const label = submit.querySelector("span");
  const status = form.querySelector("[data-project-form-status]");
  let pending = false, draftChanged = false;
  const notify = (message, kind) => {
    status.textContent = message; status.hidden = false; status.dataset.kind = kind;
    onLayoutChange();
  };
  form.addEventListener("submit", async (event) => {
    event.preventDefault();
    if (pending || form.dataset.sent === "true" || !form.reportValidity()) return;
    const payload = new FormData(form);
    payload.set("skai_async", "1");
    pending = true; draftChanged = false; submit.disabled = true;
    label.textContent = "Sending…"; form.setAttribute("aria-busy", "true");
    notify("Sending your enquiry…", "pending");
    try {
      const response = await fetch(form.action, { method: "POST", body: payload, credentials: "same-origin", headers: { Accept: "application/json" } });
      const result = await response.json();
      if (!response.ok || !result.success) {
        notify(result.data?.message || "We could not save your enquiry. Your entries are still here. Please check the required fields and try again.", "error");
      } else {
        form.dispatchEvent(new Event('skai:enquiry-saved'));
        notify(draftChanged ? "Your submitted enquiry was saved for review. Changes made while sending have not been submitted." : "Thank you. Your enquiry has been saved for review. This is not a project booking or confirmation.", "success");
        // Prevent an accidental second submission. Any new edit enables sending again.
        if (!draftChanged) form.dataset.sent = "true";
      }
    } catch {
      notify("We couldn’t confirm receipt. Your entries are still here. Please check your connection before trying again; the enquiry may already have been saved.", "error");
    } finally {
      pending = false; form.removeAttribute("aria-busy");
      submit.disabled = form.dataset.sent === "true";
      label.textContent = submit.disabled ? "Enquiry saved" : "Send enquiry";
      status.focus();
    }
  });
  const enableEditedDraft = () => {
    if (pending) { draftChanged = true; return; }
    if (form.dataset.sent !== "true") return;
    delete form.dataset.sent; submit.disabled = false; label.textContent = "Send enquiry";
    notify("Your draft has changed. Send it when you’re ready to submit a new enquiry.", "draft");
  };
  form.addEventListener("input", enableEditedDraft);
  form.addEventListener("change", enableEditedDraft);
  window.addEventListener("pageshow", () => {
    if (!pending && form.dataset.sent !== "true") { submit.disabled = false; label.textContent = "Send enquiry"; }
  });
}
